import axios from "axios";
import React, {startTransition, useEffect, useRef, useState} from "react";
import './Login.css';
import './LoginPage.css';
import {GoogleLogin} from "@react-oauth/google";
import {useDispatch} from "react-redux";
import {setUser, setUserCredentials} from "../redux/slice/userSlice";
import {useNavigate} from "react-router-dom";
import ApiEndpoint from "../../services/ApiEndpoint";
import {ToastContainer} from "react-toastify";
import {HELP_LINK} from "../../constants/WebPageConstants";
import {throwErrorToast} from "../common/ToastMessages";
import {Link} from "@nextui-org/react";
import {DistrictLogoCranberry} from "../Icons/Logos";
import {preloadPagesAfterLogin} from "../../pagesAfterLogin";
import dropOfHopeImage from "../../assets/images/login/drop-of-hope.webp";
import assemblyImage from "../../assets/images/login/assembly.webp";
import daptarImage from "../../assets/images/login/daptar.webp";
import riseImage from "../../assets/images/login/rise.webp";
import redImage from "../../assets/images/login/red.webp";
import exploreImage from "../../assets/images/login/explore.webp";
import nextImage from "../../assets/images/login/next.webp";
import dropOfHopeImageSmall from "../../assets/images/login/drop-of-hope-800.webp";
import assemblyImageSmall from "../../assets/images/login/assembly-800.webp";
import daptarImageSmall from "../../assets/images/login/daptar-800.webp";
import riseImageSmall from "../../assets/images/login/rise-800.webp";
import redImageSmall from "../../assets/images/login/red-800.webp";
import exploreImageSmall from "../../assets/images/login/explore-800.webp";
import nextImageSmall from "../../assets/images/login/next-800.webp";

// Drop of Hope, Daptar, R.I.S.E, R.E.D and Explore text is taken from rotaract3142.org; the site has no text for Assembly or NEXT
const SLIDES = [
    {
        image: redImage,
        imageSmall: redImageSmall,
        label: "Culture",
        title: "R.E.D",
        fullForm: "Rotaract Entertainment Destination",
        description: "The four zones celebrate their traditions and talent through cultural competitions.",
    },
    {
        image: riseImage,
        imageSmall: riseImageSmall,
        label: "Sports",
        title: "R.I.S.E",
        fullForm: "Rotaract Initiative for Sports Excellence",
        description: "The four zones of District 3142 compete for the coveted RISE Trophy.",
    },
    {
        image: assemblyImage,
        imageSmall: assemblyImageSmall,
        label: "District event",
        title: "Assembly & AARA Night",
        description: "The district comes together to recognise its clubs and Rotaractors for the year's work.",
    },
    {
        image: nextImage,
        imageSmall: nextImageSmall,
        label: "Conclave",
        title: "NEXT: The Conclave",
        description: "Speakers share their journeys and ideas with Rotaractors from across the district.",
    },
    {
        image: daptarImage,
        imageSmall: daptarImageSmall,
        label: "Education",
        title: "Daptar",
        description: "Educating underprivileged children through quality teaching and mentorship.",
    },
    {
        image: exploreImage,
        imageSmall: exploreImageSmall,
        label: "Fellowship",
        title: "Explore 3142",
        description: "Rotaractors from various clubs come together to experience Mumbai during Ganesh Chaturthi.",
    },
    {
        image: dropOfHopeImage,
        imageSmall: dropOfHopeImageSmall,
        label: "Blood donation drive",
        title: "Drop of Hope",
        description: "Donors come together to provide life-saving blood for those in need of critical care.",
    },
];

const STATS = [
    {value: 2, suffix: "k+", label: "Members registered"},
    {value: 80, suffix: "+", label: "Clubs"},
    {value: 10, suffix: "k+", label: "Happy faces"},
    {value: 20, suffix: "k+", label: "Lives changed"},
];

const THRUST_AREAS = ["Environment", "Empowerment", "Membership", "Education", "Branding", "Mental Health"];

const SLIDE_DURATION_MS = 5000;
// Clicks are ignored while a transition is still mostly on screen
const TRANSITION_LOCK_MS = 1100;
const DESKTOP_QUERY = "(min-width: 1024px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const pad = number => String(number).padStart(2, "0");

// The district logo SVG uses internal ids, so only one copy may be on the page at a time
function useIsDesktop() {
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);

    useEffect(() => {
        const media = window.matchMedia(DESKTOP_QUERY);
        const update = () => setIsDesktop(media.matches);
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);

    return isDesktop;
}

// Counts from 0 to `target` once, easing out
function useCountUp(target, durationMs = 1600, delayMs = 400) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (window.matchMedia(REDUCED_MOTION_QUERY).matches) {
            setValue(target);
            return;
        }
        let frameId;
        let start;
        const timeoutId = setTimeout(() => {
            const step = (time) => {
                if (start === undefined) start = time;
                const progress = Math.min((time - start) / durationMs, 1);
                setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));
                if (progress < 1) frameId = requestAnimationFrame(step);
            };
            frameId = requestAnimationFrame(step);
        }, delayMs);

        return () => {
            clearTimeout(timeoutId);
            cancelAnimationFrame(frameId);
        };
    }, [target, durationMs, delayMs]);

    return value;
}

function Stat({value, suffix, label, index}) {
    const count = useCountUp(value, 1600, 1000 + index * 120);
    return (
        // dt must come first in a dl group; flex-col-reverse shows the number on top
        <div className="login-stat flex flex-col-reverse" style={{"--s": index}}>
            <dt className="mt-1 text-xs text-white/70">{label}</dt>
            <dd className="text-2xl font-semibold tracking-tight text-white xl:text-3xl">{count}{suffix}</dd>
        </div>
    );
}

function ArrowIcon({className = ""}) {
    return (
        <svg className={`login-arrow ${className}`} viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.75"
                  strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

// The right half of the countdown ring (12 o'clock round to 6), in the 44-unit box the full ring used to be drawn in
function RingHalf() {
    return (
        <svg viewBox="0 0 44 44">
            <path d="M22 0.75 A21.25 21.25 0 0 1 22 43.25"/>
        </svg>
    );
}

// Splits text into letters for the kinetic title; words stay whole so lines only break between them
function SplitText({text}) {
    const words = text.split(" ");
    let letterIndex = 0;
    return (
        <>
            <span className="sr-only">{text}</span>
            <span aria-hidden="true">
                {words.map((word, wordIndex) =>
                    <React.Fragment key={wordIndex}>
                        <span className="login-word">
                            {Array.from(word).map(letter => {
                                const index = letterIndex++;
                                return <span key={index} className="login-char" style={{"--c": index}}>{letter}</span>;
                            })}
                        </span>
                        {wordIndex < words.length - 1 && " "}
                    </React.Fragment>
                )}
            </span>
        </>
    );
}

function SlideCaption({slide, index, compact, leaving = false}) {
    return (
        <div className={`login-caption ${leaving ? "is-leaving" : ""}`} aria-hidden={leaving ? "true" : undefined}>
            <p className="login-caption__label flex items-center text-xs font-medium uppercase tracking-[0.2em] text-white/70">
                <span className="tabular-nums text-white">{pad(index + 1)}</span>
                <span className="login-caption__rule" aria-hidden="true"/>
                {slide.label}
            </p>
            <p className={`mt-3 font-semibold leading-[1.05] tracking-[-0.03em] ${compact ? "text-3xl" : "text-5xl xl:text-6xl"}`}>
                <SplitText text={slide.title}/>
            </p>
            {slide.fullForm &&
                <p className={`login-caption__fade mt-2 font-medium text-white/85 ${compact ? "text-sm" : "text-lg xl:text-xl"}`}
                   style={{"--d": "0.7s"}}>
                    {slide.fullForm}
                </p>
            }
            {!compact &&
                <p className="login-caption__fade mt-4 max-w-md text-base leading-relaxed text-white/75"
                   style={{"--d": "0.8s"}}>
                    {slide.description}
                </p>
            }
        </div>
    );
}

// Cinematic slideshow: a curtain sweeps the next photo in while the old one is pushed away at a slower speed,
// the caption letters swap, and a countdown ring on the next button drives the timing
function InitiativeShowcase({compact}) {
    const showcaseRef = useRef(null);
    const lockUntil = useRef(0);
    const [reducedMotion] = useState(() => window.matchMedia(REDUCED_MOTION_QUERY).matches);
    const [slideState, setSlideState] = useState({active: 0, previous: null, direction: 1, transition: 0});
    const [isPaused, setIsPaused] = useState(false);
    // Screen readers only hear slide changes the user asked for, not every autoplay tick
    const [hasInteracted, setHasInteracted] = useState(false);

    const {active, previous, direction, transition} = slideState;
    const upcoming = (active + 1) % SLIDES.length;

    function goToSlide(index, newDirection) {
        const now = Date.now();
        if (index === active || now < lockUntil.current) return;
        lockUntil.current = now + TRANSITION_LOCK_MS;
        setSlideState(current => ({
            active: index,
            previous: current.active,
            direction: newDirection,
            transition: current.transition + 1,
        }));
    }

    const showNext = () => goToSlide(upcoming, 1);
    const showPrevious = () => goToSlide((active - 1 + SLIDES.length) % SLIDES.length, -1);

    function navigateByUser(go) {
        setHasInteracted(true);
        go();
    }

    // Desktop only: the photos drift against the cursor, the caption follows it, and a soft light tracks it
    useEffect(() => {
        const element = showcaseRef.current;
        if (!element || compact || reducedMotion || !window.matchMedia("(pointer: fine)").matches) return;

        let frameId = 0;
        let target = {x: 0, y: 0};
        const current = {x: 0, y: 0};

        const tick = () => {
            current.x += (target.x - current.x) * 0.08;
            current.y += (target.y - current.y) * 0.08;
            element.style.setProperty("--mx", current.x.toFixed(4));
            element.style.setProperty("--my", current.y.toFixed(4));
            const settled = Math.abs(target.x - current.x) < 0.001 && Math.abs(target.y - current.y) < 0.001;
            frameId = settled ? 0 : requestAnimationFrame(tick);
        };
        const start = () => {
            if (!frameId) frameId = requestAnimationFrame(tick);
        };
        const onMove = (event) => {
            const rect = element.getBoundingClientRect();
            target = {
                x: (event.clientX - rect.left) / rect.width * 2 - 1,
                y: (event.clientY - rect.top) / rect.height * 2 - 1,
            };
            start();
        };
        const onLeave = () => {
            target = {x: 0, y: 0};
            start();
        };

        element.addEventListener("pointermove", onMove);
        element.addEventListener("pointerleave", onLeave);
        return () => {
            element.removeEventListener("pointermove", onMove);
            element.removeEventListener("pointerleave", onLeave);
            cancelAnimationFrame(frameId);
            element.style.removeProperty("--mx");
            element.style.removeProperty("--my");
        };
    }, [compact, reducedMotion]);

    return (
        <aside ref={showcaseRef}
               className={`login-showcase relative overflow-hidden text-white ${compact ? "is-compact h-72" : "h-full"} ${isPaused ? "is-paused" : ""}`}
               onPointerEnter={event => event.pointerType === "mouse" && setIsPaused(true)}
               onPointerLeave={() => setIsPaused(false)}>
            <div className="login-stage" aria-hidden="true"
                 style={{
                     "--dir": direction,
                     // Next sweeps up from the bottom edge, previous sweeps down from the top
                     "--curtain-from": direction > 0 ? "100% 0 0 0" : "0 0 100% 0",
                     "--start": transition === 0 ? "200ms" : "0ms",
                     // The photo's slow zoom lasts as long as the slide
                     "--login-slide-duration": `${SLIDE_DURATION_MS}ms`,
                 }}>
                {SLIDES.map((slide, index) => {
                    const isActive = index === active;
                    const isLeaving = index === previous && !isActive;
                    // Only mount the photos in use: current, outgoing, and the next one (preloaded)
                    if (!isActive && !isLeaving && index !== upcoming) return null;

                    return (
                        <div key={slide.title} className={`login-frame ${isActive ? "is-active" : isLeaving ? "is-leaving" : ""}`}>
                            <img className="login-frame__img"
                                 src={slide.image}
                                 srcSet={`${slide.imageSmall} 800w, ${slide.image} 1200w`}
                                 sizes="(min-width: 1024px) 56vw, 100vw"
                                 alt=""
                                 decoding="async"
                                 fetchpriority={index === 0 ? "high" : "low"}/>
                        </div>
                    );
                })}
            </div>
            <div className="login-showcase__shade" aria-hidden="true"/>
            <div className="login-showcase__light" aria-hidden="true"/>

            <div className={`login-showcase__content relative flex h-full flex-col justify-between ${compact ? "p-6" : "p-12 xl:p-16"}`}>
                <div className="flex items-start justify-between gap-6">
                    <div className="login-logo-white">
                        <DistrictLogoCranberry/>
                    </div>

                    <div className="login-nav flex items-center gap-3" role="group" aria-label="Initiative photos"
                         onKeyDown={event => {
                             if (event.key === "ArrowRight") navigateByUser(showNext);
                             if (event.key === "ArrowLeft") navigateByUser(showPrevious);
                         }}>
                        <p className="mr-1 text-xs font-medium tabular-nums text-white/60" aria-hidden="true">
                            <span className="login-counter__mask">
                                <span key={transition} className="login-counter__digit text-white">{pad(active + 1)}</span>
                            </span>
                            <span className="mx-1">/</span>
                            {pad(SLIDES.length)}
                        </p>
                        <button type="button" className="login-nav__btn login-nav__btn--prev"
                                aria-label="Previous initiative" onClick={() => navigateByUser(showPrevious)}>
                            <ArrowIcon className="login-arrow--flip"/>
                        </button>
                        <button type="button" className="login-nav__btn"
                                aria-label="Next initiative" onClick={() => navigateByUser(showNext)}>
                            {/* Countdown ring: when it completes, the next photo comes in */}
                            {!reducedMotion &&
                                <span key={transition} className="login-ring" aria-hidden="true"
                                      style={{"--login-slide-duration": `${SLIDE_DURATION_MS}ms`}}>
                                    <span className="login-ring__half login-ring__half--right">
                                        <span className="login-ring__arc"><RingHalf/></span>
                                    </span>
                                    <span className="login-ring__half login-ring__half--left">
                                        <span className="login-ring__arc" onAnimationEnd={showNext}><RingHalf/></span>
                                    </span>
                                </span>
                            }
                            <ArrowIcon/>
                        </button>
                    </div>
                </div>

                <div>
                    <div className="login-captions" aria-live={hasInteracted ? "polite" : "off"}>
                        {previous !== null && previous !== active &&
                            <SlideCaption key={`out-${transition}`} slide={SLIDES[previous]} index={previous}
                                          compact={compact} leaving/>
                        }
                        <SlideCaption key={`in-${transition}`} slide={SLIDES[active]} index={active} compact={compact}/>
                    </div>

                    {!compact &&
                        <dl className="mt-10 grid grid-cols-4 gap-6 border-t border-white/20 pt-8">
                            {STATS.map((stat, index) => <Stat key={stat.label} index={index} {...stat}/>)}
                        </dl>
                    }
                </div>
            </div>
        </aside>
    );
}

const SIGN_IN_STEPS = ["Verifying your Google account", "Checking your Pranali access", "Getting your club ready"];
const SIGN_IN_STEP_MS = 1600;
// How long the finished card shows before the dashboard opens, and how long a failure shows before the button returns
const SIGN_IN_DONE_MS = 550;
const SIGN_IN_FAILED_MS = 1800;
// Longest the finished card waits for the dashboard's code on a slow connection, before opening it anyway
const DASHBOARD_WAIT_MS = 8000;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Who is signing in, read from Google's sign-in token, for the card's name and photo. Display only: the backend
// checks the token itself.
function readGoogleProfile(credential) {
    try {
        const payload = credential.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const text = decodeURIComponent([...atob(payload)].map(c => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join(""));
        const {given_name: givenName, name, picture} = JSON.parse(text);
        return {firstName: givenName || (name ?? "").split(" ")[0], picture};
    } catch (e) {
        return {};
    }
}

// Shown in place of the Google button while the sign-in is checked
function SigningInCard({phase, firstName, picture}) {
    const [step, setStep] = useState(0);
    const [photoFailed, setPhotoFailed] = useState(false);

    useEffect(() => {
        if (phase !== "working") return;
        const timer = setInterval(() => setStep(current => Math.min(current + 1, SIGN_IN_STEPS.length - 1)), SIGN_IN_STEP_MS);
        return () => clearInterval(timer);
    }, [phase]);

    const title = phase === "done" ? `Welcome back${firstName ? `, ${firstName}` : ""}!`
        : phase === "failed" ? "Couldn't sign you in"
            : firstName ? `Signing in as ${firstName}` : "Signing you in";
    const detail = phase === "done" ? "Opening your dashboard…"
        : phase === "failed" ? "Please try again"
            : `${SIGN_IN_STEPS[step]}…`;

    return (
        <div className={`login-signin is-${phase}`} role="status" aria-live="polite">
            <span className="login-signin__avatar">
                {picture && !photoFailed
                    ? <img className="login-signin__photo" src={picture} alt="" referrerPolicy="no-referrer"
                           onError={() => setPhotoFailed(true)}/>
                    : <span className="login-signin__photo">{(firstName || "?").slice(0, 1).toUpperCase()}</span>}
                <span className="login-signin__orbit" aria-hidden="true"/>
                <span className="login-signin__badge" aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="10" height="10">
                        <path d={phase === "failed" ? "M4 4l8 8M12 4l-8 8" : "M3.5 8.5l3 3 6-7"} fill="none" stroke="currentColor"
                              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </span>
            <span className="login-signin__text">
                <span className="login-signin__title">{title}</span>
                <span key={detail} className="login-signin__detail">{detail}</span>
            </span>
            <span className="login-signin__bar" aria-hidden="true"/>
        </div>
    );
}

function Login() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // "idle" shows the Google button; "working", "done" and "failed" show the signing-in card
    const [signIn, setSignIn] = useState({phase: "idle"});
    const isDesktop = useIsDesktop();

    async function authenticateUser(data) {
        setSignIn({phase: "working", ...readGoogleProfile(data.credential)});
        // Fetch the dashboard while the sign-in is checked, so it opens straight away afterwards
        const dashboardReady = preloadPagesAfterLogin().catch(() => null);

        const response = await getJwtResponse(data.credential);
        if (undefined !== response && null !== response && response.status === 202) {
            dispatch(setUser(response.data));
            dispatch(setUserCredentials(data.credential));
            setSignIn(current => ({...current, phase: "done"}));
            // Let the bar finish and the welcome show, and the dashboard's code arrive if it's still coming
            const [, isDashboardReady] = await Promise.all([
                wait(SIGN_IN_DONE_MS),
                Promise.race([dashboardReady.then(() => true), wait(DASHBOARD_WAIT_MS).then(() => false)]),
            ]);
            if (isDashboardReady)
                navigate("/pranali");
            else
                // Still downloading on a very slow connection: as a transition, this card stays up instead of a spinner
                startTransition(() => navigate("/pranali"));
        } else {
            setSignIn(current => ({...current, phase: "failed"}));
            await wait(SIGN_IN_FAILED_MS);
            setSignIn({phase: "idle"});
        }
    }

    function getJwtResponse(credential) {
        const apiEndPoint = new ApiEndpoint();
        return axios(apiEndPoint.getJwtResponse(credential)).then(res => {
            return res;
        }).catch(e => throwErrorToast(e));
    }

    return (
        <>
            <ToastContainer/>
            <div className="flex min-h-screen flex-col bg-white text-foreground lg:grid lg:h-screen lg:grid-cols-[1.15fr_1fr]">
                <InitiativeShowcase compact={!isDesktop}/>

                <main className="login-panel relative flex flex-1 flex-col overflow-hidden px-6 py-8 sm:px-12 lg:min-h-0 lg:overflow-y-auto">
                    <div className="relative flex flex-1 items-center justify-center py-8">
                        <div className="w-full max-w-sm">
                            <p className="login-reveal text-sm font-medium text-[#d41c68]" style={{"--login-delay": "0ms"}}>
                                Fellowship through service
                            </p>
                            {/* Each word rises from behind its own mask */}
                            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-foreground">
                                <span className="login-hword"><span style={{"--w": 0}}>Welcome</span></span>{" "}
                                <span className="login-hword"><span style={{"--w": 1}}>to</span></span>{" "}
                                <span className="login-highlight">
                                    <span className="login-hword"><span style={{"--w": 2}}>Pranali</span></span>
                                </span>
                            </h1>
                            <p className="login-reveal mt-3 text-base leading-relaxed text-default-600"
                               style={{"--login-delay": "160ms"}}>
                                The reporting and membership portal of Rotaract District 3142.
                            </p>

                            <p className="login-reveal mt-8 text-sm font-medium text-default-500"
                               style={{"--login-delay": "220ms"}}>
                                Sign in with your Google account to continue
                            </p>

                            <div className={`login-reveal mt-3 flex min-h-[44px] ${signIn.phase === "idle" ? "login-google" : ""}`} style={{"--login-delay": "260ms"}}>
                                {signIn.phase === "idle"
                                    ? <GoogleLogin useOneTap logo_alignment="left" shape="rectangular"
                                                   size="large" text="signin_with" width="320"
                                                   onSuccess={(data) => authenticateUser(data)}/>
                                    : <SigningInCard {...signIn}/>
                                }
                            </div>

                            <p className="login-reveal mt-4 text-sm text-default-500" style={{"--login-delay": "300ms"}}>
                                Trouble signing in?{" "}
                                <Link href={HELP_LINK} target="_blank" rel="noopener noreferrer" size="sm"
                                      className="font-medium text-[#d41c68]">
                                    Contact support
                                </Link>
                            </p>

                            <div className="login-reveal mt-10 border-t border-default-200 pt-6"
                                 style={{"--login-delay": "380ms"}}>
                                <p className="text-xs font-medium uppercase tracking-[0.16em] text-default-400">
                                    Thrust areas
                                </p>
                                <ul className="mt-3 flex flex-wrap gap-2">
                                    {THRUST_AREAS.map((area, index) =>
                                        <li key={area}
                                            style={{"--login-chip": index}}
                                            className="login-chip rounded-full border border-default-200 px-3 py-1 text-xs font-medium text-default-600">
                                            {area}
                                        </li>
                                    )}
                                </ul>
                            </div>

                            {!isDesktop &&
                                <dl className="login-reveal mt-8 grid grid-cols-2 gap-4" style={{"--login-delay": "440ms"}}>
                                    {STATS.map(stat =>
                                        <div key={stat.label} className="flex flex-col-reverse rounded-xl bg-default-50 px-4 py-3">
                                            <dt className="text-xs text-default-500">{stat.label}</dt>
                                            <dd className="text-xl font-semibold text-foreground">{stat.value}{stat.suffix}</dd>
                                        </div>
                                    )}
                                </dl>
                            }
                        </div>
                    </div>

                    <footer className="relative mx-auto flex w-full max-w-sm flex-wrap items-center justify-between gap-2 text-xs text-default-400">
                        <span>A Team Information Technology initiative</span>
                        <Link href="https://rotaract3142.org/" target="_blank" rel="noopener noreferrer" size="sm"
                              className="text-xs text-default-400 hover:text-[#d41c68]">
                            rotaract3142.org
                        </Link>
                    </footer>
                </main>
            </div>
        </>
    );
}

export default Login;
